import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, AlertTriangle, ShieldAlert, ChevronLeft, ChevronRight, Info, UserSearch, CircleAlert, Database, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { MOCK_MEMBERS, MemberListItem, PropensityTier, LifecycleStage } from '../lib/mock-members';
import { Button } from '@/components/ui/Button';

// Mirrors MembersListController (GET /api/members): cursor-paginated, `totalCount` not `total`.
// Reading a field the API does not send crashed the page on `total.toLocaleString()` the moment
// real demo JWTs made the call succeed (the old mock token got 401 → mock-data fallback masked it).
interface MembersListResponse {
  members: MemberListItem[];
  nextCursor: string | null;
  totalCount: number;
}

/** One core.member row from GET /api/core/sandbox/members (every value arrives as a string). */
type SandboxRow = Record<string, string | null | undefined>;

interface SandboxBrowseResponse {
  members: SandboxRow[];
  totalCount?: number | null;
  nextAfter?: string | null;
  /** The API returns one SandboxQueryMeta per response; older builds sent an array. */
  meta?: SbMeta | SbMeta[];
}

interface SbMeta { latencyMs: number; source?: 'uptiq' | 'local' }

function sbName(r: SandboxRow): string {
  const preferred = (r['preferred_name'] ?? '').trim();
  const first = preferred.length > 0 ? preferred : (r['first_name'] ?? '').trim();
  return [first, (r['last_name'] ?? '').trim()].filter((x) => x.length > 0).join(' ') || `Member ${r['member_number'] ?? ''}`;
}

function sbText(v: string | null | undefined): React.ReactElement | string {
  const t = (v ?? '').trim();
  return t.length > 0 ? t : <span className="text-ink-faint">—</span>;
}

// ---- Formatters ----

function fmtCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---- Tier badge ----

function TierBadge({ tier }: { tier: PropensityTier }): React.ReactElement {
  const map: Record<PropensityTier, { label: string; cls: string }> = {
    high:   { label: 'High',   cls: 'bg-running-soft text-running-ink' },
    medium: { label: 'Medium', cls: 'bg-brand-tint text-brand-dark' },
    low:    { label: 'Low',    cls: 'bg-sunken text-ink-soft' },
    none:   { label: '—',      cls: 'bg-bg text-ink-muted' },
  };
  const { label, cls } = map[tier];
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', cls)}>
      {label}
    </span>
  );
}

// ---- Lifecycle badge ----

function LifecycleBadge({ stage }: { stage: LifecycleStage }): React.ReactElement {
  const map: Record<LifecycleStage, { label: string; cls: string; dot: string }> = {
    new:     { label: 'New',      cls: 'bg-pass-soft text-pass',   dot: 'bg-pass' },
    active:  { label: 'Active',   cls: 'bg-brand-tint text-brand-dark',   dot: 'bg-brand' },
    at_risk: { label: 'At Risk',  cls: 'bg-warn-soft text-warn', dot: 'bg-warn-fill' },
    dormant: { label: 'Dormant',  cls: 'bg-sunken text-ink-soft',  dot: 'bg-line-strong' },
    churned: { label: 'Churned',  cls: 'bg-fail-soft text-fail',     dot: 'bg-fail' },
  };
  const { label, cls, dot } = map[stage];
  return (
    <span className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium', cls)}>
      <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', dot)} />
      {label}
    </span>
  );
}

// ---- Empty state ----

function EmptyState({ query }: { query: string }): React.ReactElement {
  return (
    <tr>
      <td colSpan={7} className="py-16 text-center">
        <div className="flex flex-col items-center gap-2">
          <UserSearch className="h-10 w-10 text-ink-faint" aria-hidden="true" />
          <p className="text-sm text-ink-muted">
            {query.length > 0
              ? `No members found for "${query}"`
              : 'No members found'}
          </p>
        </div>
      </td>
    </tr>
  );
}

// ---- Main page ----

function getJwt(): string {
  return localStorage.getItem('crm_dev_token') ?? '';
}

export function MembersPage(): React.ReactElement {
  const navigate = useNavigate();

  const [members, setMembers] = useState<MemberListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingMock, setUsingMock] = useState(false);

  // Symitar sandbox view: shown when the CRM member store has nothing to list (this deployment's
  // CRM store is the Phase-1 in-memory stub; the sandbox is the real data). Labelled, never silent.
  const [source, setSource] = useState<'crm' | 'sandbox'>('crm');
  const [sbRows, setSbRows] = useState<SandboxRow[]>([]);
  const [sbTotal, setSbTotal] = useState<number | null>(null);
  const [sbNext, setSbNext] = useState<string | null>(null);
  const [sbLoading, setSbLoading] = useState(false);
  const [sbError, setSbError] = useState<string | null>(null);
  const [sbLatencyMs, setSbLatencyMs] = useState<number | null>(null);
  const [sbSource, setSbSource] = useState<'uptiq' | 'local'>('uptiq');
  const [sbLastName, setSbLastName] = useState('');
  const [sbSearched, setSbSearched] = useState(false);

  const [query, setQuery] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState<LifecycleStage | 'all'>('all');
  const [tierFilter, setTierFilter] = useState<PropensityTier | 'all'>('all');
  const [page, setPage] = useState(1);
  const pageSize = 25;
  // The API is cursor-paginated (nextCursor), not page-numbered, so the numbered pager keeps its own
  // cursor stack: cursors[i] is the cursor that fetches page i+1 (cursors[0] = null → page 1, no
  // cursor). Page 1 previously never sent a cursor for ANY page — every "Next" click silently
  // re-fetched page 1's own data forever. A ref, not state: it must never itself retrigger the fetch
  // effect below (that would refetch the same page in a loop the moment it's populated).
  const cursorsRef = useRef<Array<string | null>>([null]);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cursor = cursorsRef.current[page - 1] ?? null;
      const params = new URLSearchParams({
        limit: String(pageSize),
        ...(cursor !== null && { cursor }),
        ...(query.length > 0 && { search: query }),
        ...(lifecycleFilter !== 'all' && { lifecycleStage: lifecycleFilter }),
        ...(tierFilter !== 'all' && { propensityTier: tierFilter }),
      });
      const res = await fetch(`/api/members?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getJwt()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as MembersListResponse;
      setMembers(data.members);
      setTotal(typeof data.totalCount === 'number' ? data.totalCount : data.members.length);
      setUsingMock(false);
      // Record the cursor for the NEXT page the first time we learn it — don't overwrite an entry
      // paging back to an already-visited page would otherwise clobber (harmless here since it's the
      // same lookup either way, but keeping the stack append-only is the simpler invariant to reason about).
      if (cursorsRef.current.length === page) cursorsRef.current = [...cursorsRef.current, data.nextCursor ?? null];
      const unfiltered = query.length === 0 && lifecycleFilter === 'all' && tierFilter === 'all';
      if (data.members.length === 0 && unfiltered) setSource('sandbox');
    } catch {
      // API not running in dev — fall back to mock data
      const filtered = MOCK_MEMBERS.filter((m) => {
        const matchesQuery =
          query.length === 0 ||
          m.displayName.toLowerCase().includes(query.toLowerCase()) ||
          m.memberNumber.includes(query) ||
          m.email.toLowerCase().includes(query.toLowerCase());
        const matchesLifecycle = lifecycleFilter === 'all' || m.lifecycleStage === lifecycleFilter;
        const matchesTier = tierFilter === 'all' || m.propensityTier === tierFilter;
        return matchesQuery && matchesLifecycle && matchesTier;
      });
      setMembers(filtered);
      setTotal(filtered.length);
      setUsingMock(true);
    } finally {
      setLoading(false);
    }
  }, [query, lifecycleFilter, tierFilter, page]);

  useEffect(() => {
    void fetchMembers();
  }, [fetchMembers]);

  // Reset to page 1 (and forget every cursor we'd learned under the old filters) on filter change.
  useEffect(() => {
    setPage(1);
    cursorsRef.current = [null];
  }, [query, lifecycleFilter, tierFilter]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  /** Browse (no lastName) or search (lastName) the Symitar sandbox. `append` pages with keyset `after`. */
  // Silent background fetches for browse pages the user hasn't clicked to yet, keyed by the `after`
  // cursor. Errors are swallowed here (null) rather than surfaced — a failed prefetch just falls
  // through to a normal, error-surfacing fetch when the user actually clicks "Load 10 more".
  const prefetchRef = useRef<Map<string, Promise<SandboxBrowseResponse | null>>>(new Map());

  const prefetchAfter = useCallback((after: string) => {
    if (prefetchRef.current.has(after)) return;
    const params = new URLSearchParams({ limit: '10', after });
    const promise = fetch(`/api/core/sandbox/members?${params.toString()}`, { headers: { Authorization: `Bearer ${getJwt()}` } })
      .then((res) => (res.ok ? (res.json() as Promise<SandboxBrowseResponse>) : null))
      .catch(() => null);
    prefetchRef.current.set(after, promise);
  }, []);

  // Core Lookup opens the exact same member-bundle endpoint (GET .../members/:n?txnLimit=5) this
  // page's rows link to. Warming the SANDBOX'S BACKEND CACHE on hover — not any client-side state —
  // means Core Lookup needs no changes at all: it just finds the entry already warm when the click
  // lands. Debounced (400ms of continuous hover) so scanning past rows while scrolling never fires
  // an agent call; only pausing on a row — which is what "about to click" looks like — does.
  //
  // Verified live 2026-09-11 with precise timestamps: SandboxCoreService.query() shares one in-flight
  // agent call across concurrent callers of the SAME sql, so a click that lands WHILE the hover's
  // prefetch is still running does not pay for a second round trip — it just waits out whatever's
  // left of the first one. Member 100003: prefetch fired at hover+450ms, the agent call took 15.2s
  // end to end, a 15s hover left ~150ms of it outstanding, and the click rendered in 0.79s. The
  // real cost is only a hover that never turns into a click within the ~15 min cache window: that's
  // 4 real agent calls spent on a member nobody opened. Fired at most once per member per page visit.
  const hoverPrefetched = useRef<Set<string>>(new Set());
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const armMemberPrefetch = useCallback((memberNumber: string) => {
    if (!memberNumber || hoverPrefetched.current.has(memberNumber)) return;
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      if (hoverPrefetched.current.has(memberNumber)) return;
      hoverPrefetched.current.add(memberNumber);
      fetch(`/api/core/sandbox/members/${encodeURIComponent(memberNumber)}?txnLimit=5`, { headers: { Authorization: `Bearer ${getJwt()}` } }).catch(() => {
        hoverPrefetched.current.delete(memberNumber); // a failed prefetch shouldn't block a real retry on click
      });
    }, 400);
  }, []);

  const disarmMemberPrefetch = useCallback(() => {
    if (hoverTimer.current) { clearTimeout(hoverTimer.current); hoverTimer.current = null; }
  }, []);

  const loadSandbox = useCallback(async (opts: { after?: string | null; lastName?: string; append?: boolean }) => {
    setSbLoading(true); setSbError(null);
    try {
      const isBrowse = !opts.lastName || opts.lastName.trim().length === 0;
      let data: SandboxBrowseResponse | null = null;
      // The Members page prefetches the next browse page as soon as the current one renders (below);
      // if the user's click lands after that finished, this is an instant, network-free page turn.
      if (isBrowse && opts.after && prefetchRef.current.has(opts.after)) {
        data = await prefetchRef.current.get(opts.after)!;
        prefetchRef.current.delete(opts.after);
      }
      if (data === null) {
        const params = new URLSearchParams({ limit: '10' });
        if (opts.lastName && opts.lastName.trim().length > 0) params.set('lastName', opts.lastName.trim());
        if (opts.after) params.set('after', opts.after);
        const res = await fetch(`/api/core/sandbox/members?${params.toString()}`, { headers: { Authorization: `Bearer ${getJwt()}` } });
        if (res.status === 503) throw new Error('The core banking sandbox is not configured on this environment.');
        if (res.status === 504) throw new Error('The sandbox agent timed out. Try again.');
        if (!res.ok) throw new Error(`Sandbox request failed (HTTP ${res.status}).`);
        data = (await res.json()) as SandboxBrowseResponse;
      }
      setSbRows((prev) => (opts.append ? [...prev, ...data.members] : data.members));
      setSbTotal(typeof data.totalCount === 'number' ? data.totalCount : (opts.lastName ? data.members.length : null));
      setSbNext(data.nextAfter ?? null);
      const metas = data.meta === undefined ? [] : Array.isArray(data.meta) ? data.meta : [data.meta];
      setSbLatencyMs(metas.length > 0 ? metas.reduce((a, m) => a + (m.latencyMs ?? 0), 0) : null);
      setSbSource(metas[0]?.source === 'local' ? 'local' : 'uptiq');
      setSbSearched(Boolean(opts.lastName));
      if (isBrowse && data.nextAfter) prefetchAfter(data.nextAfter);
    } catch (err) {
      setSbError((err as Error).message);
    } finally {
      setSbLoading(false);
    }
  }, [prefetchAfter]);

  useEffect(() => {
    if (source === 'sandbox' && sbRows.length === 0 && !sbLoading && sbError === null && !sbSearched) void loadSandbox({});
  }, [source]);

  // Clear a pending hover-prefetch timer if the page unmounts mid-hover (e.g. the user navigates away).
  useEffect(() => () => disarmMemberPrefetch(), [disarmMemberPrefetch]);

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-semibold text-ink">Members</h2>
          <p className="text-sm text-ink-muted mt-0.5">
            360-degree member profiles, lifecycle events, and propensity scores
          </p>
        </div>
        {usingMock && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-warn-soft text-warn border border-warn-soft">
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
            Demo data — API offline
          </span>
        )}
        {source === 'sandbox' && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand/10 text-brand-dark border border-brand/20">
            <Database className="h-3.5 w-3.5" aria-hidden="true" />
            {sbSource === 'local' ? 'Source: synthetic core sandbox (local)' : 'Source: Symitar sandbox (live via Uptiq)'}
            {sbLatencyMs !== null ? ` · ${(sbLatencyMs / 1000).toFixed(sbLatencyMs < 1000 ? 1 : 0)}s` : ''}
          </span>
        )}
      </div>

      {source === 'sandbox' && (
        <>
          {/* Sandbox search: exact last name (the sandbox agent runs fixed SQL — no substring search). */}
          <form
            onSubmit={(e) => { e.preventDefault(); void loadSandbox({ lastName: sbLastName }); }}
            className="flex flex-wrap items-center gap-3"
          >
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search sandbox by last name…"
                value={sbLastName}
                onChange={(e) => setSbLastName(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-line rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
              />
            </div>
            <Button size="sm" type="submit" disabled={sbLoading}>
              Search
            </Button>
            {sbSearched && (
              <button type="button" onClick={() => { setSbLastName(''); setSbSearched(false); void loadSandbox({}); }} className="text-sm text-brand-dark underline">
                Clear
              </button>
            )}
            <span className="text-sm text-ink-muted ml-auto">
              {sbLoading && sbRows.length === 0 ? 'Querying Symitar sandbox…' : sbTotal !== null ? `${sbRows.length} of ${sbTotal.toLocaleString()} members` : `${sbRows.length} members`}
            </span>
          </form>

          {sbError !== null && (
            <div role="alert" className="flex items-center gap-2 rounded-lg border border-fail-soft bg-fail-soft px-4 py-3 text-sm text-fail">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
              {sbError}
              <button type="button" onClick={() => void loadSandbox(sbSearched ? { lastName: sbLastName } : {})} className="ml-auto underline">Retry</button>
            </div>
          )}

          <div className="bg-white rounded-xl border border-line overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-bg border-b border-line">
                  <tr>
                    {['Member', 'Status', 'Type', 'Risk tier', 'Joined', 'Contact', 'Employer'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-ink-muted uppercase tracking-wide first:px-5">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-sunken">
                  {sbLoading && sbRows.length === 0 ? (
                    <tr><td colSpan={7} className="py-16 text-center text-sm text-ink-muted"><Loader2 className="inline h-4 w-4 animate-spin mr-2" aria-hidden="true" />Querying the Symitar sandbox — a cold load takes 10–15 seconds; recent pages are cached.</td></tr>
                  ) : sbRows.length === 0 && sbError === null ? (
                    <EmptyState query={sbSearched ? sbLastName : ''} />
                  ) : (
                    sbRows.map((r) => (
                      <tr
                        key={r['member_number'] ?? r['member_id'] ?? sbName(r)}
                        onClick={() => void navigate(`/core-lookup?member=${encodeURIComponent(r['member_number'] ?? '')}`)}
                        onMouseEnter={() => armMemberPrefetch(r['member_number'] ?? '')}
                        onMouseLeave={disarmMemberPrefetch}
                        className="hover:bg-brand/5 cursor-pointer transition-colors group"
                      >
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-on-brand text-xs font-semibold flex-shrink-0 bg-brand" aria-hidden="true">
                              {sbName(r).split(' ').slice(0, 2).map((n) => n[0]).join('')}
                            </div>
                            <div>
                              <p className="font-medium text-ink group-hover:text-brand-dark transition-colors">{sbName(r)}</p>
                              <p className="text-xs text-ink-muted">#{r['member_number']}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-ink-soft">{sbText(r['member_status'])}</td>
                        <td className="px-4 py-3 text-ink-soft">{sbText(r['member_type'])}</td>
                        <td className="px-4 py-3 text-ink-soft">{sbText(r['risk_tier'])}</td>
                        <td className="px-4 py-3 text-ink-soft">{(r['join_date'] ?? '').length > 0 ? fmtDate(r['join_date'] as string) : sbText(null)}</td>
                        <td className="px-4 py-3 text-ink-soft">{sbText(r['preferred_contact_method'])}</td>
                        <td className="px-4 py-3 text-ink-soft">{sbText(r['employer'])}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {sbNext !== null && !sbSearched && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-sunken">
                <span className="text-xs text-ink-muted">Rows open in Core Lookup (accounts, loans, transactions, AI insights).</span>
                <button type="button" disabled={sbLoading} onClick={() => void loadSandbox({ after: sbNext, append: true })} className="px-3 py-1.5 text-sm font-medium rounded-lg border border-line bg-white hover:bg-bg disabled:opacity-60">
                  {sbLoading ? 'Loading…' : 'Load 10 more'}
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {source === 'crm' && (<>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted"
            aria-hidden="true"
          />
          <input
            type="text"
            placeholder="Search name, member #, email..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-line rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent"
          />
        </div>

        {/* Lifecycle filter */}
        <select
          value={lifecycleFilter}
          onChange={(e) => setLifecycleFilter(e.target.value as LifecycleStage | 'all')}
          className="text-sm border border-line rounded-lg px-3 py-2 bg-white text-ink-soft focus:outline-none focus:ring-2 focus:ring-brand"
        >
          <option value="all">All Stages</option>
          <option value="new">New</option>
          <option value="active">Active</option>
          <option value="at_risk">At Risk</option>
          <option value="dormant">Dormant</option>
          <option value="churned">Churned</option>
        </select>

        {/* Propensity tier filter */}
        <select
          value={tierFilter}
          onChange={(e) => setTierFilter(e.target.value as PropensityTier | 'all')}
          className="text-sm border border-line rounded-lg px-3 py-2 bg-white text-ink-soft focus:outline-none focus:ring-2 focus:ring-brand"
        >
          <option value="all">All Tiers</option>
          <option value="high">High Propensity</option>
          <option value="medium">Medium Propensity</option>
          <option value="low">Low Propensity</option>
        </select>

        <span className="text-sm text-ink-muted ml-auto">
          {total.toLocaleString()} member{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-line overflow-hidden shadow-sm">
        {error !== null && (
          <div className="px-5 py-3 bg-fail-soft border-b border-fail-soft text-sm text-fail flex items-center gap-2">
            <CircleAlert className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-ink-muted uppercase tracking-wide border-b border-sunken bg-bg">
                <th className="px-5 py-3 font-medium">Member</th>
                <th className="px-4 py-3 font-medium">Stage</th>
                <th className="px-4 py-3 font-medium">Propensity</th>
                <th className="px-4 py-3 font-medium">Top Product</th>
                <th className="px-4 py-3 font-medium text-right">Balance</th>
                <th className="px-4 py-3 font-medium">Last Activity</th>
                <th className="px-4 py-3 font-medium">Flags</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-bg">
              {loading ? (
                // Loading skeletons
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-5 py-3">
                      <div className="flex flex-col gap-1.5">
                        <div className="h-3.5 bg-line rounded w-36" />
                        <div className="h-3 bg-sunken rounded w-24" />
                      </div>
                    </td>
                    <td className="px-4 py-3"><div className="h-5 bg-sunken rounded-full w-16" /></td>
                    <td className="px-4 py-3"><div className="h-5 bg-sunken rounded-full w-14" /></td>
                    <td className="px-4 py-3"><div className="h-3.5 bg-sunken rounded w-20" /></td>
                    <td className="px-4 py-3 text-right"><div className="h-3.5 bg-sunken rounded w-16 ml-auto" /></td>
                    <td className="px-4 py-3"><div className="h-3.5 bg-sunken rounded w-24" /></td>
                    <td className="px-4 py-3"><div className="h-3.5 bg-sunken rounded w-6" /></td>
                  </tr>
                ))
              ) : members.length === 0 ? (
                <EmptyState query={query} />
              ) : (
                members.map((member) => (
                  <tr
                    key={member.memberId}
                    onClick={() => void navigate(`/members/${member.memberId}`)}
                    className="hover:bg-brand/5 cursor-pointer transition-colors group"
                  >
                    {/* Member identity */}
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-on-brand text-xs font-semibold flex-shrink-0 bg-brand"
                          aria-hidden="true"
                        >
                          {member.displayName.split(' ').slice(0, 2).map((n) => n[0]).join('')}
                        </div>
                        <div>
                          <p className="font-medium text-ink group-hover:text-brand-dark transition-colors">
                            {member.displayName}
                          </p>
                          <p className="text-xs text-ink-muted">#{member.memberNumber}</p>
                        </div>
                      </div>
                    </td>

                    {/* Lifecycle */}
                    <td className="px-4 py-3">
                      <LifecycleBadge stage={member.lifecycleStage} />
                    </td>

                    {/* Propensity tier */}
                    <td className="px-4 py-3">
                      <TierBadge tier={member.propensityTier} />
                    </td>

                    {/* Top product */}
                    <td className="px-4 py-3 text-ink-soft">
                      {member.topProduct ?? <span className="text-ink-faint">—</span>}
                    </td>

                    {/* Balance */}
                    <td className="px-4 py-3 text-right font-medium text-ink">
                      {fmtCurrency(member.totalBalance)}
                    </td>

                    {/* Last activity */}
                    <td className="px-4 py-3 text-ink-muted whitespace-nowrap">
                      {fmtDate(member.lastActivity)}
                    </td>

                    {/* Flags */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {member.dqDays > 0 && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-warn-soft text-warn"
                            title={`${member.dqDays} days delinquent`}
                          >
                            <AlertTriangle className="h-2.5 w-2.5" />
                            {member.dqDays}d
                          </span>
                        )}
                        {member.openBsaAlerts > 0 && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-fail-soft text-fail"
                            title={`${member.openBsaAlerts} open BSA alert(s)`}
                          >
                            <ShieldAlert className="h-2.5 w-2.5" />
                            BSA
                          </span>
                        )}
                        {member.dqDays === 0 && member.openBsaAlerts === 0 && (
                          <span className="text-ink-faint text-xs">—</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && total > pageSize && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-sunken bg-bg">
            <p className="text-xs text-ink-muted">
              Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total.toLocaleString()}
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={loading || page === 1}
                className="p-1.5 rounded text-ink-muted hover:bg-line disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-xs text-ink-soft px-2">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={loading || page === totalPages}
                className="p-1.5 rounded text-ink-muted hover:bg-line disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </>)}
    </div>
  );
}
